"use client"

import styled from "@emotion/styled"
import Image from "next/image"
import Link from "next/link"
import { FC, useMemo } from "react"
import { findProperty, footerMenuClick, getNonUmbracoUrl, getUmbracoMediaUrl } from "utils"
import { Mint2Icon } from "../../icons"

type FooterLink = {
  name?: string | null
  url?: string | null
  target?: string | null
}

type FooterProps = {
  data: any
  companyName: string
  locale: string
}

const StyledFooter = styled.footer`
  width: 100%;
  background-color: ${(props) => props.theme.colors.footerColor};
  color: ${(props) => props.theme.colors.footerTextColor};
`

const FooterContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  padding: 24px 16px;
  margin: 0 auto;
  width: 100%;

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 40px 20px;
    max-width: 1080px;
    display: grid;
    grid-template-columns: 130px 1fr 130px;
  }

  ${(props) => props.theme.breakpoints.up("lg")} {
    max-width: 1480px;
  }
`

const LogoLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 16px;
  text-decoration: none;
  color: ${(props) => props.theme.colors.footerTextColor};
  font-weight: 600;
  letter-spacing: 0.02em;
  transition: opacity 0.2s ease;

  &:hover,
  &:focus-visible {
    opacity: 0.8;
  }

  img {
    width: 69px;
    height: 43px;
    ${(props) => props.theme.breakpoints.up("md")} {
      width: 100px;
      height: 61px;
    }
  }
`

const LogoFallback = styled.span`
  font-size: 18px;
  font-weight: 600;
  line-height: 1.4;
`

const FooterNav = styled.nav`
  width: 100%;
  display: flex;
  justify-content: center;
`

const LinksGrid = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  justify-self: center;
  grid-template-columns: repeat(2, auto);
  gap: 24px;
  justify-content: center;
  justify-items: center;
  width: max-content;

  ${(props) => props.theme.breakpoints.up("md")} {
    display: flex;
    justify-content: center;
    gap: 32px;
  }
`

const LinkItem = styled.li`
  display: flex;
`

const StyledLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease-in;

  &:hover,
  &:focus-visible {
    font-weight: 700;
  }
`

const CopyText = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  width: max-content;
  justify-self: flex-end;
  cursor: pointer;
  transition: opacity 0.2s ease;
  &:hover {
    opacity: 0.7;
  }
  p {
    margin: 0;
    padding: 0;
    font-size: 12px;
    line-height: 20px;
    font-weight: 400;
  }
`

const normalizeHref = (url?: string | null) => {
  if (!url) {
    return "#"
  }

  return getNonUmbracoUrl(url)
}

const Footer: FC<FooterProps> = ({ data, companyName, locale }) => {
  const homeHref = locale === "en" ? "/en" : "/"

  const logo = useMemo(() => findProperty(data, "logo")?.mediaItems?.[0], [data])
  const links = useMemo<FooterLink[]>(() => {
    const linkPicker = findProperty(data, "links")
    if (linkPicker?.links && Array.isArray(linkPicker.links)) {
      return linkPicker.links
    }
    return []
  }, [data])

  const handleFooterClick = (label: string) => {
    footerMenuClick(label, companyName)
  }

  const renderFooterLink = (link: FooterLink, index: number, variant: "primary" | "secondary") => {
    if (!link?.url) return null
    const href = normalizeHref(link.url)
    const label = link?.name?.trim() || `${companyName} link ${index + 1}`
    const key = `${label}-${href}-${index}-${variant}`
    const className = variant === "secondary" ? "secondary" : undefined

    return (
      <StyledLink
        key={key}
        href={href}
        className={className}
        onClick={() => handleFooterClick(label)}
      >
        {label}
      </StyledLink>
    )
  }

  if (!data) {
    return null
  }
  return (
    <StyledFooter role="contentinfo">
      <FooterContent>
        {logo ? (
          <LogoLink
            href={homeHref}
            aria-label={`${companyName} home`}
            onClick={() => handleFooterClick("Footer logo")}
          >
            <Image
              src={getUmbracoMediaUrl(logo.url)}
              alt={logo.altText || companyName}
              width={100}
              height={61}
              style={{ objectFit: "contain" }}
              sizes="(max-width: 768px) 43px, 69px"
            />
          </LogoLink>
        ) : (
          <LogoFallback>{companyName}</LogoFallback>
        )}
        {links.length > 0 && (
          <FooterNav aria-label={locale === "he" ? "תפריט תחתון" : "Footer navigation"}>
            <LinksGrid role="presentation">
              {links.map((link, index) => (
                <LinkItem key={(link?.name || "link") + index.toString()}>
                  {renderFooterLink(link, index, "primary")}
                </LinkItem>
              ))}
            </LinksGrid>
          </FooterNav>
        )}

        <a
          href="https://www.mintapp.co.il/"
          target="_blank"
          rel="noreferrer"
          style={{ textDecoration: "none", color: "inherit" }}
        >
          <CopyText>
            <Mint2Icon />
            <p>Created By</p>
          </CopyText>
        </a>
      </FooterContent>
    </StyledFooter>
  )
}

export default Footer
